import type { AnalyticsAudience, AnalyticsCrewStatus } from '../lib/analytics'
import type { Group } from '../types'
import { useAuth } from './useAuth'
import { useGroups } from './useGroups'

// The team's own crews. Members are friends who got the app from us, so their
// usage says little about how strangers find and use Beacon.
const INNER_CIRCLE_GROUP_IDS: ReadonlySet<string> = new Set([
  '10iwV0QAKF4mZQQkIYYS', // SE Baybies
  'JY29XvDgn2n9C4qVzIEX', // Churu
  'vvzanew3uVZKnlcYFRp5', // Promesa Borracha
  'tvDtHACy0vrcQhfiqMbL', // Mas Atentos Que Dormidos
])

function crewStatus(groups: Group[]): AnalyticsCrewStatus {
  if (groups.length === 0) return 'none'
  return groups.some(g => g.memberIds.length > 1) ? 'crew' : 'solo'
}

/**
 * Who the viewer is to us — `team` (admin), `inner_circle` (in one of the team's
 * crews) or `organic` — plus their crew status. Both are null until auth and
 * groups resolve, so a crew member is never briefly reported as organic / none.
 * Backs the GA4 user properties, and gates features still in internal rollout.
 */
export function useAudience(): { audience: AnalyticsAudience | null; crew: AnalyticsCrewStatus | null } {
  const { signedInUid, isAdmin, loading: authLoading } = useAuth()
  const { groups, loading: groupsLoading } = useGroups(signedInUid)

  const memberGroups = groups.filter(g => g.memberIds.includes(signedInUid ?? ''))
  const groupsKnown = !authLoading && !(signedInUid && groupsLoading)
  const crew = groupsKnown ? crewStatus(memberGroups) : null
  const audience: AnalyticsAudience | null = isAdmin
    ? 'team'
    : !groupsKnown
      ? null
      : memberGroups.some(g => INNER_CIRCLE_GROUP_IDS.has(g.id))
        ? 'inner_circle'
        : 'organic'
  return { audience, crew }
}
